import { JUNIOR_ELIGIBLE_SLOTS } from "./tiers";
import { getOccupiedSlots, getGroupSchedule, parseTime } from "./time";

// Two scheduled groups clash when they occupy the same court on the same day
// and their time ranges overlap. A group missing a day, court, or start time
// isn't really scheduled yet, so it can't clash with anything.
export function groupsScheduleOverlap(a, b) {
  if (!a || !b) return false;
  if (!a.day || !a.court || !b.day || !b.court) return false;
  if (a.day !== b.day || a.court !== b.court) return false;
  const sa = getGroupSchedule(a);
  const sb = getGroupSchedule(b);
  if (!sa.startTime || !sa.duration || !sb.startTime || !sb.duration) return false;
  const startA = parseTime(sa.startTime);
  const startB = parseTime(sb.startTime);
  if (startA == null || startB == null) return false;
  const endA = startA + sa.duration * 60;
  const endB = startB + sb.duration * 60;
  // Half-open ranges: a session ending exactly when another starts is fine.
  return startA < endB && startB < endA;
}

// Like groupsScheduleOverlap but court-agnostic: two groups clash for a
// person (a coach) if they run at overlapping times on the same day, no
// matter which court — one coach can't staff two sessions at once.
export function groupsTimeOverlap(a, b) {
  if (!a || !b) return false;
  if (!a.day || !b.day || a.day !== b.day) return false;
  const sa = getGroupSchedule(a);
  const sb = getGroupSchedule(b);
  if (!sa.startTime || !sa.duration || !sb.startTime || !sb.duration) return false;
  const startA = parseTime(sa.startTime);
  const startB = parseTime(sb.startTime);
  if (startA == null || startB == null) return false;
  const endA = startA + sa.duration * 60;
  const endB = startB + sb.duration * 60;
  return startA < endB && startB < endA;
}

// Every other group that `group` clashes with (same court/day, overlapping
// time). Compares by id, so a group is never reported as conflicting with
// itself even if an unsaved copy is passed in.
export function getScheduleConflicts(group, allGroups) {
  if (!group) return [];
  return allGroups.filter(g => g.id !== group.id && groupsScheduleOverlap(group, g));
}

// The set of group ids that clash with at least one other group — handy for
// badging a whole list in one pass instead of an O(n²) check per render.
export function getConflictingGroupIds(allGroups) {
  const ids = new Set();
  for (let i = 0; i < allGroups.length; i++) {
    for (let j = i + 1; j < allGroups.length; j++) {
      if (groupsScheduleOverlap(allGroups[i], allGroups[j])) {
        ids.add(allGroups[i].id);
        ids.add(allGroups[j].id);
      }
    }
  }
  return ids;
}

// Parses a player's free-text preferredPartners field into a list of
// matching players from the roster (fuzzy substring match on name).
export function getPreferredPartners(player, allPlayers) {
  if (!player.preferredPartners) return [];
  const val = player.preferredPartners.toLowerCase().trim();
  if (["no", "n/a", "not sure yet", "none", ""].includes(val)) return [];

  // Split on commas, ampersands, slashes
  const names = player.preferredPartners
    .split(/[,;&/]/)
    .map(n => n.trim().toLowerCase())
    .filter(n => n.length > 1);

  if (names.length === 0) return [];

  return names
    .map(name => allPlayers.find(p =>
      p.id !== player.id &&
      (p.name.toLowerCase().includes(name) || name.includes(p.name.toLowerCase()))
    ))
    .filter(Boolean);
}

export function hasUnmetPartnerPreference(player, allPlayers) {
  const partners = getPreferredPartners(player, allPlayers);
  if (partners.length === 0) return false;

  // Flag if any found partner is not in the same group
  return partners.some(partner =>
    !player.groupId || partner.groupId !== player.groupId
  );
}

// A group's time range can straddle two hourly availability checkboxes
// (e.g. a 3:30 PM start touches both "3-4pm" and "4-5pm"). Only flag a
// conflict if the player checked NONE of the hours the session touches —
// requiring every touched hour would falsely flag players whose real
// availability covers the session but who only checked one of the two
// boundary hours.
export function hasAvailabilityConflict(player, group) {
  const slots = getOccupiedSlots(group);
  if (!group?.day || slots.length === 0) return false;
  if (!player.availability?.length) return false;
  return slots.every(slot => !player.availability.includes(`${group.day}|${slot}`));
}

export function getWarnings(player, targetGroup) {
  const warnings = [];
  const slots = getOccupiedSlots(targetGroup);

  if (targetGroup.playerIds.length >= 4) {
    warnings.push("Court already has 4 players");
  }

  if (player.classification !== "Junior" && targetGroup.tier.startsWith("Junior")) {
    warnings.push(`${player.name} is an adult being moved to a junior group`);
  }

  if (player.classification === "Junior" && !targetGroup.tier.startsWith("Junior")) {
    warnings.push(`${player.name} is a junior being moved to an adult group`);
  }

  if (
    player.classification === "Junior" &&
    (player.age === null || player.age > 6) &&
    targetGroup.tier === "Junior Mini (Pickleball)"
  ) {
    warnings.push(`${player.name} is over 6 and should not be in Junior Mini`);
  }

  if (
    player.classification === "Junior" &&
    player.age !== null &&
    player.age <= 6 &&
    targetGroup.tier !== "Junior Mini (Pickleball)"
  ) {
    warnings.push(`${player.name} is age 6 or under and should be in Junior Mini (Pickleball)`);
  }

  if (
    player.classification === "Junior" &&
    slots.length > 0 &&
    !slots.every(s => JUNIOR_ELIGIBLE_SLOTS.includes(s))
  ) {
    warnings.push(`${player.name} cannot be scheduled outside junior-eligible hours`);
  }

  if (targetGroup.day && slots.length > 0) {
    if (slots.every(slot => !player.availability.includes(`${targetGroup.day}|${slot}`))) {
      warnings.push(`${player.name} did not list this slot as available`);
    }
  }

  return warnings;
}
